import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home as HomeIcon, AlertTriangle, MapPinned, Bug } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api, resolveMediaUrl } from "@/lib/api";
import { trackPageview } from "@/lib/analytics";
import { useLang } from "@/i18n/LangContext";

export default function NotFound() {
    const location = useLocation();
    const { t, lp } = useLang();
    const [signUrl, setSignUrl] = useState("");
    const [modInfo, setModInfo] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/mod-info");
                setSignUrl(data.not_found_image_url || "");
                setModInfo(data);
            } catch {
                /* ignore */
            }
        })();
        trackPageview(location.pathname || "/404");
    }, [location.pathname]);

    return (
        <div
            data-testid="not-found-page"
            className="min-h-screen flex flex-col bg-[color:var(--bg-base)]"
        >
            <SEO
                modInfo={modInfo}
                pathname={location.pathname || "/404"}
                title="404 - Nie znaleziono strony"
                description="Strona, której szukasz, nie istnieje. Wróć na stronę główną Project Ruda Śląska."
                noindex
            />
            <Header />
            <main className="flex-1 flex items-center pt-32 pb-24">
                <div className="max-w-4xl mx-auto px-6 md:px-12 w-full">
                    <div className="label-eyebrow mb-4 flex items-center gap-2">
                        <AlertTriangle size={14} /> {t("Błąd 404")}
                    </div>

                    {/* Big 404 + dead-end road sign */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10 md:gap-14 mb-6">
                        <h1
                            data-testid="not-found-code"
                            className="font-display uppercase leading-none tracking-tighter text-[color:var(--primary)] text-[22vw] sm:text-[9rem] md:text-[13rem]"
                            style={{ lineHeight: 0.85 }}
                        >
                            404
                        </h1>

                        {/* Custom uploaded sign (from admin) or default SVG fallback */}
                        {signUrl ? (
                            <img
                                src={resolveMediaUrl(signUrl)}
                                alt="Znak drogowy: ślepa droga"
                                data-testid="not-found-sign"
                                className="w-40 sm:w-44 md:w-56 shrink-0 object-contain drop-shadow-2xl"
                            />
                        ) : (
                            <svg
                                data-testid="not-found-sign"
                                viewBox="0 0 100 100"
                                role="img"
                                aria-label="Znak drogowy: ślepa droga"
                                className="w-40 sm:w-44 md:w-56 shrink-0 drop-shadow-2xl"
                            >
                                <rect x="0" y="0" width="100" height="100" rx="11" fill="#ffffff" />
                                <rect x="3" y="3" width="94" height="94" rx="8.5" fill="#1b3aa0" />
                                {/* red barrier with white outline */}
                                <rect
                                    x="20"
                                    y="19"
                                    width="60"
                                    height="15"
                                    rx="1.5"
                                    fill="#d8231d"
                                    stroke="#ffffff"
                                    strokeWidth="2"
                                />
                                {/* white vertical stem (the road) */}
                                <rect x="44.5" y="33" width="11" height="52" fill="#ffffff" />
                            </svg>
                        )}
                    </div>

                    <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tighter text-white mb-5">
                        {t("Zjechałeś z drogi w ślepy zaułek")}
                    </h2>

                    <p className="text-base text-[color:var(--text-secondary)] leading-relaxed max-w-xl mb-3">
                        {t("Tej strony nie ma na naszej trasie. Mogła zostać przeniesiona, usunięta albo adres jest po prostu błędny.")}
                    </p>

                    {location?.pathname && (
                        <p className="text-xs text-[color:var(--text-muted)] font-mono break-all mb-10">
                            {t("Nieznany adres:")} <span className="text-[color:var(--primary)]">{location.pathname}</span>
                        </p>
                    )}

                    <div className="flex flex-wrap gap-3">
                        <Link to={lp("/")} data-testid="not-found-home-btn" className="btn-primary">
                            <HomeIcon size={14} /> {t("Wróć na stronę główną")}
                        </Link>
                        <Link
                            to={lp("/#mapa")}
                            data-testid="not-found-map-btn"
                            className="inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider font-semibold rounded-md border border-white/15 text-[color:var(--text-secondary)] hover:text-white hover:border-white/30 transition-colors"
                        >
                            <MapPinned size={14} /> {t("Interaktywna mapa")}
                        </Link>
                        <Link
                            to={lp("/zglos-blad")}
                            data-testid="not-found-report-btn"
                            className="inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider font-semibold rounded-md border border-white/15 text-[color:var(--text-secondary)] hover:text-white hover:border-white/30 transition-colors"
                        >
                            <Bug size={14} /> {t("Zgłoś błąd")}
                        </Link>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
